// The destinations a tenant has configured.
//
// One bordered card each rather than table rows: a destination carries a
// failure sentence and its recovery action, which is two lines of prose that a
// cell cannot hold. A tenant can have at most 25, so nothing here needs
// virtualizing or a column picker.

import { NOTE_CLASS } from "../../lib/ui.js";
import { describeRoutes } from "../../lib/pushNotificationRoutes.js";
import { failureCopy } from "../../lib/pushNotificationErrors.js";
import { PUSH_VENDORS, vendorByWire } from "../../lib/pushNotificationVendors.js";
import { secondaryBtnCls } from "./formControls.jsx";

/** Every vendor, as prose: "A", "A or B", "A, B or C". */
function vendorNames() {
    const labels = PUSH_VENDORS.map((v) => v.label);
    if (labels.length <= 1) return labels.join("");
    return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}

/** How a destination's batching window reads on the card. */
function describeWindow(integration) {
    const set = integration.windowSecondsSet ?? integration.window_seconds_set ?? false;
    if (!set) return "every minute";
    const seconds = Number(integration.windowSeconds ?? integration.window_seconds ?? 0);
    if (seconds < 60) return `every ${seconds} seconds`;
    const minutes = Math.round(seconds / 60);
    return `every ${minutes} minute${minutes === 1 ? "" : "s"}`;
}

/**
 * A destination's state as one word and one colour.
 *
 * Keyed on `lastError`, not on the failure streak: a destination rejected
 * before we ever send -- an unreadable credential, a URL we refuse -- never
 * advances the streak, and used to read as "no notifications delivered yet".
 */
function statusOf(integration) {
    const lastError = integration.lastError ?? integration.last_error ?? "";
    if (!integration.enabled) return { label: "Paused", dot: "var(--muted-soft)", lastError: "" };
    if (lastError) return { label: "Failing", dot: "var(--red, #c0392b)", lastError };
    return { label: "Delivering", dot: "var(--green)", lastError: "" };
}

function DestinationCard({ integration, signalNamesById, canManage, onToggle, onRemove }) {
    const spec = vendorByWire(integration.vendor);
    const state = statusOf(integration);
    const failure = state.lastError ? failureCopy(state.lastError) : null;
    const lastDelivery = integration.lastDeliveryAt ?? integration.last_delivery_at ?? null;
    const lastFailure = integration.lastFailureAt ?? integration.last_failure_at ?? null;

    return (
        <li className="grid gap-[6px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[12px] py-[10px]">
            <div className="flex items-start justify-between gap-[10px]">
                <span className="grid gap-[3px] min-w-0">
                    <span className="flex items-center gap-[8px] min-w-0">
                        <span
                            aria-hidden="true"
                            className="h-[8px] w-[8px] shrink-0 rounded-full"
                            style={{ background: state.dot }}
                        />
                        <span className="text-[13px] font-medium truncate text-[color:var(--ink)]">
                            {integration.displayName ?? integration.display_name}
                        </span>
                        <span className={`${NOTE_CLASS} shrink-0`}>{state.label}</span>
                    </span>
                    <span className={`${NOTE_CLASS} block`}>
                        {spec?.label ?? "Unknown"}
                        {" - "}
                        {describeRoutes(integration.routes, signalNamesById)}
                        {" - "}
                        {describeWindow(integration)}
                    </span>
                    {/* The only positive signal on the card: without it a
                        working destination and one that has never delivered
                        look identical. */}
                    <span className={`${NOTE_CLASS} block`}>
                        {lastDelivery
                            ? `Last delivered ${new Date(lastDelivery).toLocaleString()}`
                            : "No notifications delivered yet"}
                    </span>
                </span>
                <span className="flex items-center gap-[8px] shrink-0">
                    <button type="button" onClick={() => onToggle(integration)} className={secondaryBtnCls}>
                        {integration.enabled ? "Pause" : "Resume"}
                    </button>
                    {canManage ? (
                        <button type="button" onClick={() => onRemove(integration)} className={secondaryBtnCls}>
                            Remove
                        </button>
                    ) : null}
                </span>
            </div>
            {/* The recovery mechanism, not a footnote: retries span seconds, so
                a failed notification is not coming later. For a destination the
                customer configured, the customer is the only person who can fix
                it -- so the reason and the time are on screen rather than only
                in our metrics. */}
            {failure ? (
                <span className="block text-[11px] leading-[1.22] text-red-400" role="alert">
                    {failure.summary}
                    {lastFailure ? ` (last tried ${new Date(lastFailure).toLocaleString()})` : ""}.{" "}
                    {failure.action}
                </span>
            ) : null}
        </li>
    );
}

export default function DestinationList({
    integrations,
    signalNamesById,
    canManage,
    onToggle,
    onRemove,
}) {
    if (integrations.length === 0) {
        return (
            <div className="grid gap-[3px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[12px] py-[14px]">
                <p className="text-[13px] font-medium text-[color:var(--ink)]">
                    No destinations yet
                </p>
                <p className={NOTE_CLASS}>
                    {/* Built from the vendor table, not spelled here: this was
                        the one place the vendor list appeared outside
                        PUSH_VENDORS, so a third vendor shipped with a sentence
                        that omitted it. */}
                    Use &quot;Add destination&quot; to send detections to {vendorNames()}.
                </p>
            </div>
        );
    }
    return (
        <ul className="grid gap-[6px]">
            {integrations.map((integration) => (
                <DestinationCard
                    key={integration.id}
                    integration={integration}
                    signalNamesById={signalNamesById}
                    canManage={canManage}
                    onToggle={onToggle}
                    onRemove={onRemove}
                />
            ))}
        </ul>
    );
}
